import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Download, FileText, Info } from 'lucide-react';
import {
  useClientReportContent,
  type ClientReportSection,
} from '../../lib/api/queries';
import { NarrativePreview } from '../audit/report/NarrativePreview';
import {
  EvidenceRegisterTable,
  SectionEvidenceTable,
  evidenceForSectionKey,
  type EvidenceRow,
} from '../audit/report/EvidenceTable';
import { clientEvidenceDownloadUrl } from '../../lib/api/queries';

/**
 * Full-page read-only view of a single published report. Layout:
 *
 *   <Header>
 *     ← Back to reports
 *     Audit title + org + published date + Download PDF button
 *   </Header>
 *
 *   <Sections>
 *     Narrative → rendered inline via Tiptap read-only editor
 *     Computed  → "See the PDF for scorecard / heatmap / etc." placeholder
 *   </Sections>
 *
 * Computed sections (scorecard, heatmap, action prioritisation, timeline)
 * aren't rendered inline in v1 — they'd need per-computed-kind client
 * endpoints, and the PDF is the canonical deliverable for full detail.
 * The Download PDF button up top makes the PDF one click away.
 */
export function ClientReportViewerPage() {
  const { snapshotId } = useParams<{ snapshotId: string }>();
  const { data: report, isLoading, error } = useClientReportContent(snapshotId);

  if (isLoading) {
    return <p className="text-sm text-gray-500">Loading…</p>;
  }

  if (error || !report) {
    return (
      <div className="space-y-4">
        <Link to="/client/reports"
              className="inline-flex items-center gap-1.5 text-sm text-accent-alt-600
                         hover:text-accent-alt-700">
          <ArrowLeft className="w-4 h-4" aria-hidden />
          Back to reports
        </Link>
        <div className="rounded-md border border-danger-200 bg-danger-50 p-4">
          <p className="text-sm text-danger-800">
            This report isn't available. It may have been retracted by DPAS
            staff, or the link is no longer valid. Go back to your reports
            list to see what's currently published.
          </p>
        </div>
      </div>
    );
  }

  const orderedSections = [...report.sections]
    .sort((a, b) => a.displayOrder - b.displayOrder);

  // Slice 17 — evidence frozen into this published version. The server
  // already returned it in canonical order with its reference codes
  // assigned, so this only reshapes it for the shared table. Downloads go
  // through the snapshot-scoped route: an evidence id on its own carries
  // no right to read the file.
  const evidenceRows: EvidenceRow[] = report.evidence.map(e => ({
    id:            e.id,
    sectionId:     e.sectionId,
    sectionCode:   e.sectionCode,
    criterionCode: e.criterionCode,
    itemText:      e.itemText,
    referenceCode: e.referenceCode,
    fileName:      e.fileName,
    sizeBytes:     e.sizeBytes,
    description:   e.description,
    itemComment:   e.itemComment,
    uploadedAtUtc: e.uploadedAtUtc,
    href:          clientEvidenceDownloadUrl(report.snapshotId, e.id),
  }));

  return (
    <div className="space-y-6">
      <Link to="/client/reports"
            className="inline-flex items-center gap-1.5 text-sm text-accent-alt-600
                       hover:text-accent-alt-700">
        <ArrowLeft className="w-4 h-4" aria-hidden />
        Back to reports
      </Link>

      <header className="border-b border-gray-200 pb-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold text-accent-alt-600 uppercase tracking-wide">
              {report.organisationName}
            </p>
            <h1 className="text-3xl font-semibold text-gray-900 mt-1">
              {report.auditTitle}
            </h1>
            <p className="text-sm text-gray-500 mt-2">
              {report.label ? <span className="font-medium">{report.label}</span> : null}
              {report.label ? ' · ' : null}
              Published {formatDate(report.publishedAtUtc)}
            </p>
          </div>
          <a
            href={`/api/client/reports/${report.snapshotId}/pdf`}
            download
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium
                       text-white bg-accent-alt-600 hover:bg-accent-alt-700 rounded-md
                       transition-colors shrink-0"
          >
            <Download className="w-4 h-4" aria-hidden /> Download PDF
          </a>
        </div>
      </header>

      <ol className="space-y-8">
        {orderedSections.map(section => (
          <li key={section.id} id={sectionAnchor(section)}>
            <SectionView section={section} evidenceRows={evidenceRows} />
          </li>
        ))}
      </ol>

      <div className="border-t border-gray-200 pt-6 text-center">
        <a
          href={`/api/client/reports/${report.snapshotId}/pdf`}
          download
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium
                     text-white bg-accent-alt-600 hover:bg-accent-alt-700 rounded-md
                     transition-colors"
        >
          <Download className="w-4 h-4" aria-hidden /> Download the full report as PDF
        </a>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------

function SectionView({
  section, evidenceRows,
}: {
  section: ClientReportSection;
  evidenceRows: EvidenceRow[];
}) {
  const heading = (
    <header className="mb-3 pb-2 border-b border-gray-100">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
        Section {section.displayOrder}
      </p>
      <h2 className="text-xl font-semibold text-gray-900 mt-0.5">
        {section.title}
      </h2>
    </header>
  );

  if (section.kind === 'Narrative') {
    return (
      <section>
        {heading}
        <NarrativePreview contentJson={section.contentJson} />
        {/* Slice 17 — the evidence provided against this section, with
            working download links. Same table the staff preview and the
            PDF use. */}
        <SectionEvidenceTable
          rows={evidenceForSectionKey(section.key, evidenceRows)}
        />
      </section>
    );
  }

  // The evidence register is the one computed section the client viewer
  // can render in full — it needs no scoring data, just the frozen list
  // that came down with the report.
  if (section.computedKind === 'evidenceregister') {
    return (
      <section>
        {heading}
        <EvidenceRegisterTable rows={evidenceRows} />
      </section>
    );
  }

  // Computed section — client viewer defers to the PDF for the details.
  // Cheaper than shipping per-computed-kind client endpoints in v1.
  return (
    <section>
      {heading}
      <div className="rounded-md border border-gray-200 bg-gray-50 p-4 flex gap-3">
        {section.computedKind === 'cover' || section.computedKind === 'tableofcontents' ? (
          <Info className="w-5 h-5 text-gray-400 shrink-0 mt-0.5" aria-hidden />
        ) : (
          <FileText className="w-5 h-5 text-gray-400 shrink-0 mt-0.5" aria-hidden />
        )}
        <div>
          <p className="text-sm text-gray-700">
            {describeComputed(section.computedKind)}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Download the PDF above to see the full table, chart, and figures
            for this section.
          </p>
        </div>
      </div>
    </section>
  );
}

function describeComputed(kind: string | null): string {
  switch (kind) {
    case 'cover':
      return "This section is the report's title page — organisation, assessment date, and overall compliance score.";
    case 'tableofcontents':
      return 'Table of contents linking every section of the report.';
    case 'scopeareas':
      return 'Standard scope-of-audit boilerplate covering the ten data-protection areas assessed.';
    case 'scorecardtable':
      return 'Compliance scorecard summarising your performance in each area with a red-to-green band.';
    case 'heatmap':
      return 'Chart showing the compliance percentage for every section.';
    case 'actionprioritisation':
      return 'Prioritised list of remediation actions, ordered by priority (High → Low).';
    case 'actiontimeline':
      return 'Remedial-action timeline placing actions in three-month windows over the next year.';
    case 'evidenceregister':
      return 'Index of every document provided as evidence during the assessment.';
    default:
      return 'Computed content available in the PDF version.';
  }
}

function sectionAnchor(section: ClientReportSection): string {
  return `section-${section.displayOrder}`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    day: 'numeric', month: 'long', year: 'numeric',
  });
}
